import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useReducedMotion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { glassCardProps } from '@/styles/glassCard';
import { closeMenuOverlay, selectIsMenuOverlayOpen } from '@/store/slices/uiSlice';
import { incrementGradient } from '@/store/slices/gradientSlice';
import { useMenuItems } from '@/pages/menu';
import { menuGateBannerCopy } from '@/copy';

/**
 * Locked sheet/modal motion values (design spec §4.5) — not a placeholder.
 * Drives the JS mount/unmount timeout, so it stays a numeric literal here;
 * must match `--motion-sheet-duration` in `variables.css` exactly.
 */
const OPEN_TRANSITION_MS = 280;

/**
 * Single shared Menu overlay (D45b Phase 3) — a glass bottom sheet on
 * mobile, a glass popover on desktop. Opened from the mobile tab bar's
 * Menu item, the desktop rail's `⋯`, and the redirecting `/menu` route.
 * Sources its content from `useMenuItems()` so it never forks from the
 * legacy full-screen page's item list or quiz-gate banner.
 *
 * Mounted once near the app root (see TabLayout) so every trigger just
 * dispatches `openMenuOverlay()`/`closeMenuOverlay()` on `uiSlice` — no
 * per-trigger content duplication.
 */
export function MenuOverlay() {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsMenuOverlayOpen);
  const isDesktop = useIsDesktop();
  const prefersReducedMotion = useReducedMotion();
  const { items, needsQuiz, handleTakeQuizClick } = useMenuItems();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Two-stage mount/visible state drives the enter/exit transition:
  // `mounted` controls whether the overlay is in the DOM at all, `visible`
  // controls the transform/opacity that CSS actually transitions between.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  // Opening the overlay is Redux state, not a pathname change, so the
  // root-level RouteChangeHandler's pathname-watch never fires for it —
  // dispatch the same incrementGradient action explicitly here so the
  // overlay participates in the same "advances on every navigation"
  // behavior route changes already get (reuses gradientSlice, doesn't
  // rebuild it).
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      dispatch(incrementGradient());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (!mounted) return undefined;

    if (isOpen) {
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }

    setVisible(false);
    const timeout = window.setTimeout(
      () => setMounted(false),
      prefersReducedMotion ? 0 : OPEN_TRANSITION_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [mounted, isOpen, prefersReducedMotion]);

  // Keyed on `visible` (not `isOpen`) — the dialog isn't in the DOM yet on
  // the render where `isOpen` first flips true (that render only sets
  // `mounted`), so a ref-based focus call keyed on `isOpen` alone would
  // silently no-op against an unattached ref.
  useEffect(() => {
    if (!visible) return undefined;

    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dispatch(closeMenuOverlay());
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, dispatch]);

  if (!mounted) {
    return null;
  }

  const handleClose = () => dispatch(closeMenuOverlay());

  const handleItemClick = (onClick: () => void) => {
    onClick();
    handleClose();
  };

  const transitionValue = 'transform var(--motion-sheet-duration) var(--motion-sheet-easing), opacity var(--motion-sheet-duration) var(--motion-sheet-easing)';

  return (
    <Portal>
      {/* Backdrop — dims on mobile, transparent (click-catcher only) on
          desktop per the design spec's popover treatment. */}
      <Box
        position="fixed"
        inset={0}
        zIndex="modal"
        bg={isDesktop ? 'transparent' : 'blackAlpha.600'}
        onClick={handleClose}
        aria-hidden="true"
      />
      <Box
        {...glassCardProps}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        onClick={(event) => event.stopPropagation()}
        position="fixed"
        zIndex="modal"
        display="flex"
        flexDirection="column"
        {...(isDesktop
          ? { left: '14px', bottom: '78px', w: '300px', maxH: '70vh' }
          : { left: 0, right: 0, bottom: 0, w: 'full', maxH: '80vh', borderBottomRadius: 0 })}
        overflowY="auto"
        transform={visible ? 'translateY(0)' : 'translateY(20px)'}
        opacity={visible ? 1 : 0}
        p={4}
        sx={{
          ...glassCardProps.sx,
          WebkitBackdropFilter: glassCardProps.backdropFilter,
          transition: transitionValue,
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
          '@supports not (backdrop-filter: blur(1px))': {
            bg: 'blackAlpha.900',
            backdropFilter: 'none',
          },
        }}
      >
        <HStack justify="space-between" mb={needsQuiz ? 2 : 4}>
          <Text fontWeight="semibold" fontSize="lg" color="white">
            Menu
          </Text>
          <IconButton
            ref={closeButtonRef}
            aria-label="Close menu"
            icon={<X size={18} />}
            variant="ghost"
            size="sm"
            color="gray.300"
            minW="44px"
            minH="44px"
            _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
            onClick={handleClose}
          />
        </HStack>

        {needsQuiz && (
          <Box bg="orange.500" borderRadius="xl" p={4} mb={3}>
            <HStack spacing={3} align="start">
              <Text color="white" fontWeight="semibold" flex="1" fontSize="sm">
                {menuGateBannerCopy.description}
              </Text>
              <Button
                size="sm"
                bg="white"
                color="orange.600"
                _hover={{ bg: 'orange.50' }}
                onClick={() => handleItemClick(handleTakeQuizClick)}
                flexShrink={0}
              >
                {menuGateBannerCopy.ctaLabel}
              </Button>
            </HStack>
          </Box>
        )}

        <VStack spacing={2} align="stretch">
          {items.map((item) => {
            const isLocked = needsQuiz && item.requiresQuiz;
            return (
              <Box key={item.title} bg="white" borderRadius="xl" overflow="hidden" boxShadow="sm">
                <Button
                  onClick={() => handleItemClick(item.onClick)}
                  isDisabled={isLocked}
                  opacity={isLocked ? 0.4 : 1}
                  cursor={isLocked ? 'not-allowed' : 'pointer'}
                  variant="ghost"
                  size="lg"
                  w="full"
                  h="auto"
                  p={3}
                  justifyContent="flex-start"
                  _hover={{
                    bg: item.isDestructive ? 'red.50' : item.isDebug ? 'orange.50' : 'gray.50',
                  }}
                  borderRadius="xl"
                  bg={item.isDestructive ? 'red.50' : item.isDebug ? 'orange.50' : 'white'}
                >
                  <HStack spacing={3} align="center" w="full">
                    <Box
                      p={2}
                      borderRadius="lg"
                      bg={item.isDestructive ? 'red.100' : item.isDebug ? 'orange.100' : 'gray.100'}
                    >
                      <Icon
                        as={item.icon}
                        color={item.isDestructive ? 'red.600' : item.isDebug ? 'orange.600' : 'black'}
                        size={18}
                      />
                    </Box>
                    <HStack spacing={2} align="center" flex="1">
                      <Text
                        fontWeight="medium"
                        fontSize="sm"
                        color={item.isDestructive ? 'red.600' : item.isDebug ? 'orange.600' : 'black'}
                      >
                        {item.title}
                      </Text>
                    </HStack>
                  </HStack>
                </Button>
              </Box>
            );
          })}
        </VStack>
      </Box>
    </Portal>
  );
}

export default MenuOverlay;
